// src/components/ProcessDialog/MobileResultCard.jsx
import React from 'react';
import {
    Box,
    Stack,
    Typography,
    Chip,
    Divider,
    Paper,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ShieldCheck } from 'lucide-react';
import { formatValue, fmtLog } from '../../../utils/mathFormatters';

const MobileResultCard = ({
    title,
    presence,
    unidade = 'NMP',
    agua = true,
    labNmp,
    predNmp,
    submitting,
    hasPresenceData,
}) => {
    const theme = useTheme();
    const isPresenca = presence === true;
    const isPending = presence === undefined || submitting;

    let cardBg = theme.palette.background.paper;
    let cardBorder = theme.palette.divider;

    if (hasPresenceData && !isPending) {
        if (isPresenca) {
            cardBg = alpha(theme.palette.error.main, 0.04);
            cardBorder = alpha(theme.palette.error.main, 0.3);
        } else {
            cardBg = alpha(theme.palette.success.main, 0.04);
            cardBorder = alpha(theme.palette.success.main, 0.3);
        }
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: cardBorder,
                bgcolor: cardBg,
                borderLeft: `6px solid ${
                    presence === true
                        ? theme.palette.error.main
                        : presence === false
                          ? theme.palette.success.main
                          : theme.palette.grey[300]
                }`,
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="subtitle1" fontWeight={700}>
                    {title}
                </Typography>
                {hasPresenceData && agua && (
                    <Chip
                        label={
                            presence === true
                                ? 'PRESENTE'
                                : presence === false
                                  ? 'AUSENTE'
                                  : 'PROCESSANDO'
                        }
                        size="small"
                        color={
                            presence === true
                                ? 'error'
                                : presence === false
                                  ? 'success'
                                  : 'default'
                        }
                        sx={{ fontWeight: 'bold' }}
                    />
                )}
            </Stack>

            <Stack spacing={2}>
                <Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        display="block"
                        mb={1}
                    >
                        {unidade === 'UFC'
                            ? 'UFC (Unidades Formadoras de Colônia)'
                            : 'NMP (Número Mais Provável)'}
                    </Typography>
                    <Box
                        sx={{
                            p: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack spacing={1}>
                            <Box>
                                <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                >
                                    Lab (LAB)
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {formatValue(labNmp, fmtLog, false)}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography
                                    variant="caption"
                                    display="block"
                                    color="primary.main"
                                >
                                    IA (PRED)
                                </Typography>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    color="primary.main"
                                    lineHeight={1}
                                >
                                    {formatValue(predNmp, fmtLog, submitting)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Box>

            </Stack>

            {hasPresenceData && !isPending && agua && (
                <Box
                    sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: '1px dashed',
                        borderColor: 'divider',
                    }}
                >
                    <Stack spacing={0.8}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ShieldCheck
                                size={14}
                                color={theme.palette.text.secondary}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={500}
                            >
                                Padrão Portaria 888:{' '}
                                {isPresenca
                                    ? 'Presença em 100 mL'
                                    : 'Ausência em 100 mL'}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="caption"
                            fontWeight={800}
                            color={isPresenca ? 'error.main' : 'success.main'}
                            sx={{ fontSize: '0.7rem' }}
                        >
                            STATUS: {isPresenca ? 'NÃO CONFORME' : 'CONFORME'}
                        </Typography>
                    </Stack>
                </Box>
            )}
        </Paper>
    );
};

export default MobileResultCard;
